import {
  BATTLE_CLAIM_STATUSES,
  BATTLE_MODES,
  BATTLE_STATUSES,
  type BattleRound,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  boolean,
  char,
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";

import {
  boxes,
  boxVersions,
  type DropTableItem,
  endUsers,
  environments,
  serverSeeds,
} from "./app.js";
import { tenant } from "./auth.js";

// ---- battles ----------------------------------------------------------------------------------
// A battle is N seats opening the same ordered rounds of boxes on one server-paced timeline. The
// whole outcome (every open, the totals, the winner) is computed and stored the moment the last
// seat fills; `current_revealed_round` then decides how much of it the API may show. Money is
// paid on the way in: a seat debits its price when it is taken (`payBattleEntry`, one
// `battle_entry` ledger row), and the claim mints items without moving money
// (`claimBattleWinnings`). See docs/battles.md.

const createdAt = timestamp("created_at", { withTimezone: true }).notNull().defaultNow();

/**
 * One battle. Writer: the public /v1 battle flow on behalf of the creating end user (create, add
 * bot, start now) and the reveal runner / sweeper (status, cursor, updated_at). Safe to return to
 * the owning tenant; `current_revealed_round` decides what the API may show of battle_opens, and a
 * tenant member's view is gated the same way while the battle runs.
 */
export const battles = pgTable(
  "battles",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    creatorEndUserId: text("creator_end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    mode: text("mode", { enum: BATTLE_MODES }).notNull(),
    playerCount: integer("player_count").notNull(),
    /** Reserved for team battles; always null in v1 (no team mode is implemented). */
    teamCount: integer("team_count"),
    /** Private battles are left out of the lobby list and joined by id. */
    isPublic: boolean("is_public").notNull().default(true),
    fastSpin: boolean("fast_spin").notNull().default(false),
    /**
     * Provably-fair domain separator for every open in this battle (`generateSalt`): 32 hex
     * characters, or 8 on a battle created before 2026-10.
     */
    salt: varchar("salt", { length: 32 }).notNull(),
    /** Σ rounds[].priceCents: what every seat pays on entry. */
    priceCents: integer("price_cents").notNull(),
    /** One entry per round: the box, the pinned drop-table version, and that round's price. */
    rounds: jsonb("rounds").$type<BattleRound[]>().notNull(),
    /**
     * What a non-winning human seat is minted at claim (`BATTLE_LOSER_PRIZE_ITEM`), snapshotted at
     * creation so the value disclosed on the page is the value minted. Null = created before the
     * prize existed: those losers are minted nothing.
     */
    loserPrizeItem: jsonb("loser_prize_item").$type<DropTableItem>(),
    status: text("status", { enum: BATTLE_STATUSES }).notNull().default("created"),
    /** Highest round whose results the API may show; -1 before the first reveal. */
    currentRevealedRound: integer("current_revealed_round").notNull().default(-1),
    /** The daily server seed every open in this battle used; set at start. */
    seedDate: char("seed_date", { length: 10 }).references(() => serverSeeds.date),
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    /** Stored at start so the runner and every client pace the same timeline. */
    revealIntervalMs: integer("reveal_interval_ms"),
    startCountdownMs: integer("start_countdown_ms"),
    /** The CREATE request's Idempotency-Key; a replay returns this row. */
    idempotencyKey: text("idempotency_key"),
    createdAt,
    /** Bumped by every reveal step: the runner's heartbeat the sweeper reads. */
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("battles_env_public_status_idx").on(t.environmentId, t.isPublic, t.status, t.id),
    index("battles_env_created_idx").on(t.environmentId, t.createdAt),
    index("battles_status_created_idx").on(t.status, t.createdAt),
    index("battles_status_updated_idx").on(t.status, t.updatedAt),
    uniqueIndex("battles_idempotency_idx")
      .on(t.environmentId, t.idempotencyKey)
      .where(sql`${t.idempotencyKey} IS NOT NULL`),
    check("battles_player_count_allowed", sql`${t.playerCount} IN (2, 3, 4, 6)`),
    check("battles_price_non_negative", sql`${t.priceCents} >= 0`),
    // Drop when team mode ships.
    check("battles_team_count_null_v1", sql`${t.teamCount} IS NULL`),
  ],
);

/**
 * One filled seat (an empty seat has no row). `end_user_id` null = bot: client_seed "bot",
 * claim_status stays `none`, nothing is paid or minted. Writer: the /v1 battle flow (join, add
 * bot), startBattle (totals, is_winner), claimBattleWinnings (claim_status) and
 * sellBackBattleWinnings (sold_at, sold_cents). `is_winner` and
 * the totals must not reach a response until the battle is completed.
 */
export const battlePlayers = pgTable(
  "battle_players",
  {
    id: text("id").primaryKey(), // ulid; the claim unit (the ledger ref is the battle id)
    battleId: text("battle_id")
      .notNull()
      .references(() => battles.id, { onDelete: "cascade" }),
    // Denormalized so the pending-reveal gate on end_user_id never joins.
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    seatIndex: integer("seat_index").notNull(),
    endUserId: text("end_user_id").references(() => endUsers.id, { onDelete: "cascade" }),
    clientSeed: varchar("client_seed", { length: 36 }).notNull(),
    isWinner: boolean("is_winner").notNull().default(false),
    /** Σ item.value over this seat's own opens: the winner rule's first key. */
    totalValueCents: integer("total_value_cents").notNull().default(0),
    /** Σ result over this seat's own opens: the winner rule's second key (≤ 50 × 999,999). */
    totalResult: integer("total_result").notNull().default(0),
    /** = battles.price_cents: what this seat paid on entry (its `battle_entry` row), winner or not. */
    costCents: integer("cost_cents").notNull(),
    claimStatus: text("claim_status", { enum: BATTLE_CLAIM_STATUSES }).notNull().default("none"),
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    /** Set together by a sell-back (`sellBackBattleWinnings`); a sold seat is also `claimed`. */
    soldAt: timestamp("sold_at", { withTimezone: true }),
    soldCents: integer("sold_cents"),
    createdAt,
  },
  (t) => [
    uniqueIndex("battle_players_seat_idx").on(t.battleId, t.seatIndex),
    uniqueIndex("battle_players_battle_end_user_idx")
      .on(t.battleId, t.endUserId)
      .where(sql`${t.endUserId} IS NOT NULL`),
    // The pending-reveal gate: an end user with an unclaimed seat may not open or join.
    index("battle_players_end_user_claim_idx").on(t.endUserId, t.claimStatus),
    index("battle_players_end_user_created_idx").on(t.endUserId, t.createdAt),
    check("battle_players_seat_range", sql`${t.seatIndex} >= 0`),
  ],
);

/**
 * One provably-fair open per (battle, round, seat). An open in the glossary sense, but it
 * consumes a battle-local nonce (round × playerCount + seat) against the battle's salt, never the
 * end user's own nonce, so it cannot live in `opens`. Writer: startBattle, once. Rows with
 * round_index above battles.current_revealed_round, and `awarded_seat_index` before completion,
 * must not reach a response.
 */
export const battleOpens = pgTable(
  "battle_opens",
  {
    id: text("id").primaryKey(), // ulid; inventory_items.battle_open_id points here
    battleId: text("battle_id")
      .notNull()
      .references(() => battles.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    roundIndex: integer("round_index").notNull(),
    seatIndex: integer("seat_index").notNull(),
    /** The seat that receives this item at claim (the winner in free-for-all); null = nobody. */
    awardedSeatIndex: integer("awarded_seat_index"),
    clientSeed: varchar("client_seed", { length: 36 }).notNull(),
    nonce: integer("nonce").notNull(),
    /** resultNumber in [0, 999999]. */
    result: integer("result").notNull(),
    itemIndex: integer("item_index").notNull(),
    item: jsonb("item").$type<DropTableItem>().notNull(),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id),
    createdAt,
  },
  (t) => [
    uniqueIndex("battle_opens_nonce_idx").on(t.battleId, t.nonce),
    uniqueIndex("battle_opens_round_seat_idx").on(t.battleId, t.roundIndex, t.seatIndex),
    index("battle_opens_awarded_idx").on(t.battleId, t.awardedSeatIndex),
  ],
);
